import React, { useEffect } from 'react';
import { RouterProvider, Route, Navigate, Outlet, createHashRouter, createRoutesFromElements, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { ErrorBoundary } from 'react-error-boundary';
import { connect } from 'react-redux';
import Form from './Form';
import UzWizard from './uzWizard/UzWizard'
import Forbidden from '../views/forbidden/Forbidden'
import Container from '../Container';
import ErrorPage from "../views/error/ErrorPage";
import withMeta from '../utils/withMeta';
import HttpsRedirect from '../utils/httpsRedirect';
import { showMsg } from '../utils/logger';
import LoadingPanel from '../LoadingPanel';
import MenuPage from '../views/menu/MenuPage';
import AppOffline from '../views/appOffline/AppOffline';

const LOGIN = "/prihlasenie";
const FORBIDDEN = "/stranka_nenajdena";

export const AuthLayout = ({ isAuth }) => {
  const location = useLocation()
  if (isAuth) {
    return <Outlet />;
  }
  return <Navigate to={LOGIN} replace state={{ from: location }} />;
};

/**
 * MetaRoute component.
 * @component
 * @param {boolean} isAuth - isAuth
 * @param {function} [componentFactory] - componentFactory
 * @param {function} [reducer] - reducer
 * @param {function} [filterFactory] - filterFactory
 * @param {function} [akcFactory] - akcFactory
 * @param {object} meta - withMeta prop
 * @param {object} history - withRouter prop
 * @param {function} [currentElFactory] - currentElFactory
 * @param {function} getComponent - getComponent
 * @param {function} initMenu - initMenu
 * @param {function} [selectAkcFactory] - selectAkcFactory
 * @param {React.Component} loginPage - Login component
 * @param {React.Component} [forbiddenPage] - Forbidden component
 * @param {Array} customRoutes - An array of custom routes
 * @param {element} userInfo - User info element
 * @param {element} stats - Stats element
 * @returns {React.Component} The rendered component for this routes
*/
const MetaRoute = (props) => {
  const { isAuth, componentFactory, reducer, filterFactory, akcFactory, meta, currentElFactory, getComponent, initMenu, selectAkcFactory, forbiddenPage, customRoutes } = props;

  useEffect(() => {
    if (meta.menuError) {
      props.showMsg(meta.menuError, "error")
    }
    // eslint-disable-next-line
  }, [meta.menuError])

  const LoginPage = props.loginPage;

  const routes = createHashRouter(createRoutesFromElements(
    <Route element={
      <ErrorBoundary FallbackComponent={ErrorPage}>
        <Container
          user={props.user}
          menu={props.menu}
          menuList={props.menuList}
          updateMenu={props.updateMenu}
          onMenuFilter={props.onMenuFilter}
          onSignOut={props.onSignOut}
          handleConfirmChangePassword={props.handleConfirmChangePassword}
          header={props.header}
          footer={props.footer}
          uzivatel={props.uzivatel}
          theme="default"
          brand={props.brand}
          themes={props.themes}
          manualProps={props.manualProps}
          getManual={props.getManual}
          getListManual={props.getListManual}
          getMessages={props.getMessages}
          getNotifications={props.getNotifications}
          userInfo={props.userInfo}
          navUser={props.navUser}
          infoPanel={props.infoPanel}
          stats={props.stats}
        />
      </ErrorBoundary>
    }
    >
      {customRoutes}

      <Route path={LOGIN} element={<LoginPage />} />

      <Route element={<AuthLayout isAuth={isAuth} />}>
        {meta?.menu.filter(x => x.ComponentId && x.NadMenuID).map(x => {
          return <Route
            path={x.Route}
            element={getComponent(x.ComponentId)}
            initMenu={initMenu}
            key={x.ComponentId}
          />
        })}

        <Route
          path="/form/:id/:db?"
          element={
            <Form
              componentFactory={componentFactory}
              filterFactory={filterFactory}
              reducer={reducer}
              akcFactory={akcFactory}
              currentElFactory={currentElFactory}
              getComponent={getComponent}
              selectAkcFactory={selectAkcFactory}
            />
          }
        />
        <Route path="/uz-wizard" element={<UzWizard />} />
        <Route path={"/menu"} element={<MenuPage />} />
        <Route path={FORBIDDEN} element={forbiddenPage || (<Forbidden />)} />
        <Route path={"*"} element={<Navigate to={FORBIDDEN} replace />} />

      </Route>

    </Route>
  ), { basename: window.config.basename })

  if (window.self !== window.top) {
    return <div style={{ textAlign: "center", margin: "25vh" }}>
      Aplikáciu nie je možné spúštať v rámci iFrame.
    </div>
  }

  if (meta.isAppOffline) return <AppOffline />

  if (meta.menu.length === 0 && isAuth && !meta.menuError) {
    return <LoadingPanel isLoading={true}></LoadingPanel>
  }


  return (
    <HttpsRedirect>
      <RouterProvider router={routes}>

      </RouterProvider>
    </HttpsRedirect>
  );
}

MetaRoute.propTypes = {
  componentFactory: PropTypes.func,
  filterFactory: PropTypes.func,
  isAuth: PropTypes.bool.isRequired,
  reducer: PropTypes.func,
  akcFactory: PropTypes.func,
  currentElFactory: PropTypes.func,
  getComponent: PropTypes.func,
  selectAkcFactory: PropTypes.func,
  initMenu: PropTypes.func.isRequired,
  loginPage: PropTypes.oneOfType([PropTypes.func, PropTypes.object]).isRequired,
  getManual: PropTypes.func,
  getListManual: PropTypes.func,
  getMessages: PropTypes.func,
  getNotifications: PropTypes.func,
  forbiddenPage: PropTypes.func,
  userInfo: PropTypes.element,
  stats: PropTypes.element
}

const mapDispatchToProps = (dispatch) => ({
  showMsg: (msg, msgType = 'ok') => dispatch(showMsg(msg, msgType))
});

export default withMeta(connect(null, mapDispatchToProps)(MetaRoute));
